import dayjs, { Dayjs } from 'dayjs';
import { RawOpenHoursDay } from 'utils/timeHelpers';

const CLOSING_SOON_MINUTES = 30;
const DAYS_IN_WEEK = 7;

export const DAY_KEYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

export type OpenHoursDays = (RawOpenHoursDay | undefined)[];

export type OpenStatus =
  | { state: 'open'; closesAt: string }
  | { state: 'closing'; minutesLeft: number }
  | { state: 'closed'; opensAt: string; dayOffset: number; dayKey: string }
  | { state: 'closed'; opensAt: null };

const toMinutes = (value?: string | null): number | null => {
  if (!value) return null;

  const plain = value.match(/^(\d{1,2}):(\d{2})/);
  if (plain) return Number(plain[1]) * 60 + Number(plain[2]);

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.hour() * 60 + parsed.minute() : null;
};

const formatMinutes = (minutes: number): string =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

const isClosed = (closed: RawOpenHoursDay['closed']) =>
  !!closed && closed !== 'false' && closed !== '0';

export const openingOf = (day?: RawOpenHoursDay) => {
  if (!day || isClosed(day.closed)) return null;

  const from = toMinutes(day.no);
  const to = toMinutes(day.lidz);

  return from !== null && to !== null && to > from
    ? {
        from: formatMinutes(from),
        to: formatMinutes(to),
        fromMinutes: from,
        toMinutes: to,
      }
    : null;
};

export const hasAnyOpenHours = (days: OpenHoursDays): boolean =>
  days.some((day) => !!openingOf(day));

export const todayIndex = (now: Dayjs = dayjs()): number =>
  (now.day() + 6) % DAYS_IN_WEEK;

export const getOpenStatus = (
  days: OpenHoursDays,
  now: Dayjs = dayjs()
): OpenStatus | null => {
  if (!hasAnyOpenHours(days)) return null;

  const currentDay = todayIndex(now);
  const nowMinutes = now.hour() * 60 + now.minute();
  const today = openingOf(days[currentDay]);

  if (
    today &&
    nowMinutes >= today.fromMinutes &&
    nowMinutes < today.toMinutes
  ) {
    const minutesLeft = today.toMinutes - nowMinutes;

    return minutesLeft <= CLOSING_SOON_MINUTES
      ? { state: 'closing', minutesLeft }
      : { state: 'open', closesAt: today.to };
  }

  for (let offset = 0; offset <= DAYS_IN_WEEK; offset++) {
    const index = (currentDay + offset) % DAYS_IN_WEEK;
    const opening = openingOf(days[index]);

    if (opening && (offset > 0 || nowMinutes < opening.fromMinutes)) {
      return {
        state: 'closed',
        opensAt: opening.from,
        dayOffset: offset,
        dayKey: DAY_KEYS[index],
      };
    }
  }

  return { state: 'closed', opensAt: null };
};

export const isOpenNow = (status: OpenStatus | null): boolean =>
  status?.state === 'open' || status?.state === 'closing';
